import { Box, Button, Stack, Typography } from '@mui/material'
import { Lock as LockIcon } from '@mui/icons-material'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { poolwerxColors } from '../../theme/colors'
import StandaloneCardLayout from '../layouts/StandaloneCardLayout'
import AuthPageFooter from '../auth/AuthPageFooter'
import { primaryTealButtonSx } from '../auth/authButtonStyles'
import type { User } from '../../services/userApi'

interface FranchiseAccessDeniedProps {
  user: User
  fpCode: string
}

/**
 * "Access Denied" card shown when a user lands on an FP they can't reach.
 * Used by InspectorLayout (valid sub-route) and NotFound (unknown sub-route),
 * so both paths give the same message instead of one showing a bare 404.
 */
export default function FranchiseAccessDenied({ user, fpCode }: FranchiseAccessDeniedProps) {
  const franchiseAccess = user.franchise_access || []
  const hasSingleFp = user.role === 'FP_USER' && franchiseAccess.length === 1
  const targetPath = hasSingleFp ? `/inspect/${franchiseAccess[0].fp_code}` : '/choose-franchise'
  const buttonLabel = hasSingleFp ? 'Go to My Franchise' : 'Choose Franchise'

  return (
    <>
      <Helmet>
        <title>Access Denied | Inspector Tool</title>
      </Helmet>
      <StandaloneCardLayout headerTitle="Access Denied">
        <Stack spacing={3}>
          <Box sx={{ textAlign: 'center' }}>
            <LockIcon sx={{ fontSize: '4rem', color: poolwerxColors.teal, mb: 2 }} />
            <Typography variant="h5" sx={{ mb: 2, color: '#333' }}>
              You don't have access to this franchise
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Your account doesn't have permission to view <strong>{fpCode.toUpperCase()}</strong>.
              Please contact your administrator if you believe this is an error.
            </Typography>
          </Box>

          <Stack spacing={2}>
            <Button
              component={Link}
              to={targetPath}
              variant="contained"
              size="large"
              fullWidth
              sx={primaryTealButtonSx}
            >
              {buttonLabel}
            </Button>
          </Stack>

          <AuthPageFooter user={user} />
        </Stack>
      </StandaloneCardLayout>
    </>
  )
}
